import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Loader2, Search, X, MapPin } from "lucide-react";
import { loadGooglePlaces } from "@/lib/googleMaps";

function getPrimaryText(prediction) {
  return (
    prediction?.structuredFormat?.mainText?.text ||
    prediction?.text?.text ||
    ""
  );
}

function getSecondaryText(prediction) {
  return (
    prediction?.structuredFormat?.secondaryText?.text ||
    prediction?.secondaryText?.text ||
    ""
  );
}

const PlaceInput = forwardRef(function PlaceInput(
  { value, onChange, onPlaceSelect, placeholder, className = "" },
  ref
) {
  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const suppressFetchValueRef = useRef("");
  const selectedValueRef = useRef("");
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  useImperativeHandle(ref, () => ({
    getValue: () => inputRef.current?.value || value || "",
    clear: () => {
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      suppressFetchValueRef.current = "";
      selectedValueRef.current = "";
      onChange("");
      setSuggestions([]);
      setHighlightedIndex(-1);
      setOpen(false);
    },
  }));

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        await loadGooglePlaces();
        if (cancelled || !window.google?.maps?.places) return;
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        setReady(true);
      } catch (error) {
        console.error("Google Places init error:", error);
      }
    }

    init();

    return () => {
      cancelled = true;
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!ready) return;

    const input = (value || "").trim();

    if (selectedValueRef.current && input === selectedValueRef.current) {
      setLoading(false);
      setSuggestions([]);
      setHighlightedIndex(-1);
      setOpen(false);
      return;
    }

    if (suppressFetchValueRef.current && input === suppressFetchValueRef.current) {
      setLoading(false);
      setSuggestions([]);
      setHighlightedIndex(-1);
      setOpen(false);
      return;
    }

    if (input !== suppressFetchValueRef.current) {
      suppressFetchValueRef.current = "";
    }

    if (input !== selectedValueRef.current) {
      selectedValueRef.current = "";
    }

    if (input.length < 2) {
      setSuggestions([]);
      setHighlightedIndex(-1);
      setLoading(false);
      setOpen(false);
      return;
    }

    let active = true;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const request = {
          input,
          sessionToken: sessionTokenRef.current,
          locationBias: {
            west: 39.0,
            south: 20.0,
            east: 51.0,
            north: 31.0,
          },
        };

        const { suggestions: nextSuggestions = [] } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        if (!active) return;

        const placePredictions = nextSuggestions
          .map((item) => item.placePrediction)
          .filter(Boolean);

        setSuggestions(placePredictions);
        setHighlightedIndex(placePredictions.length ? 0 : -1);
        setOpen(focused && !selectedValueRef.current && placePredictions.length > 0);
      } catch (error) {
        if (active) {
          console.error("Google Places suggestions error:", error);
          setSuggestions([]);
          setHighlightedIndex(-1);
          setOpen(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, 150);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [focused, ready, value]);

  const closeDropdown = () => {
    setOpen(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleSelect = async (prediction) => {
    try {
      const place = prediction.toPlace();
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location", "types", "id"],
      });

      const address = place.formattedAddress || place.displayName || getPrimaryText(prediction) || "";
      suppressFetchValueRef.current = address;
      selectedValueRef.current = address;
      onChange(address);

      if (onPlaceSelect) {
        onPlaceSelect({
          placeId: place.id || "",
          name: place.displayName || "",
          address,
          types: place.types || [],
          lat: place.location ? place.location.lat() : null,
          lng: place.location ? place.location.lng() : null,
        });
      }

      closeDropdown();
      setFocused(false);
      if (window.google?.maps?.places?.AutocompleteSessionToken) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }
      requestAnimationFrame(() => {
        inputRef.current?.blur();
      });
    } catch (error) {
      console.error("Google Places selection error:", error);
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setFocused(false);
      closeDropdown();
    }, 100);
  };

  const handleFocus = () => {
    setFocused(true);
    if (!selectedValueRef.current && (value || "").trim().length >= 2 && suggestions.length > 0) {
      setOpen(true);
    }
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      selectedValueRef.current = value || "";
      setFocused(false);
      inputRef.current?.blur();
      closeDropdown();
      return;
    }

    if (!open || suggestions.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selected = suggestions[highlightedIndex] || suggestions[0];
      if (selected) {
        await handleSelect(selected);
      }
    }
  };

  const handleInputChange = (nextValue) => {
    suppressFetchValueRef.current = "";
    selectedValueRef.current = "";
    onChange(nextValue);
  };

  const handleClear = () => {
    suppressFetchValueRef.current = "";
    selectedValueRef.current = "";
    onChange("");
    setSuggestions([]);
    setHighlightedIndex(-1);
    setOpen(false);
    setFocused(true);
    inputRef.current?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="place-input-frame flex items-center gap-3 min-w-0 rounded-[18px] border border-white/8 bg-white/[0.025] px-4 py-3 transition-colors duration-200 focus-within:border-[#C7A239]/45 focus-within:bg-white/[0.045]">
        <Search
          className={`w-4 h-4 shrink-0 transition-all duration-150 ${
            focused ? "opacity-0 scale-90" : "opacity-100 text-gray-500"
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          className="place-input-control w-full bg-transparent text-white placeholder:text-gray-500 focus:outline-none text-[15px] md:text-base leading-6"
        />
        {loading ? <Loader2 className="w-4 h-4 text-gray-500 animate-spin shrink-0" /> : null}
        {!loading && value ? (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              handleClear();
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-white/5 text-gray-500 hover:bg-white/10 hover:text-white transition-colors shrink-0"
            aria-label="Clear input"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </div>

      {open && suggestions.length > 0 ? (
        <div className="absolute top-[calc(100%+10px)] left-0 right-0 z-[120] overflow-hidden rounded-[22px] border border-white/12 bg-[#0c0d11] shadow-[0_24px_70px_rgba(0,0,0,0.58)]">
          <div className="max-h-80 overflow-y-auto py-2 custom-suggestion-scroll">
            {suggestions.map((prediction, index) => {
              const primary = getPrimaryText(prediction);
              const secondary = getSecondaryText(prediction);
              const isActive = index === highlightedIndex;

              return (
                <button
                  key={`${primary}-${secondary}-${index}`}
                  type="button"
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (blurTimeoutRef.current) {
                      clearTimeout(blurTimeoutRef.current);
                    }
                    handleSelect(prediction);
                  }}
                  className={`group flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors ${
                    isActive ? "bg-[#151821]" : "hover:bg-[#12141c]"
                  }`}
                >
                  <div className={`mt-0.5 flex h-8 w-8 items-center justify-center rounded-full border shrink-0 ${
                    isActive ? "border-[#C7A239]/35 bg-[#C7A239]/12" : "border-white/8 bg-white/[0.03]"
                  }`}>
                    <MapPin className={`w-4 h-4 ${isActive ? "text-[#C7A239]" : "text-gray-500 group-hover:text-[#C7A239]"}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm md:text-[15px] text-white leading-snug truncate">{primary}</div>
                    {secondary ? (
                      <div className="text-xs md:text-[13px] text-gray-500 mt-1 truncate">{secondary}</div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="border-t border-white/8 px-4 py-2.5 text-[10px] uppercase tracking-[0.24em] text-gray-500 bg-[#0c0d11]">
            Powered by Google
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default PlaceInput;
